// cart.slice.ts
import { createSlice } from '@reduxjs/toolkit';

import { ApiError } from '../../models/common.model';
import type { Cart } from '../../shopify/models/cart.model';
import type { Checkout } from '../../shopify/models/checkout.model';
import type { LineItem } from '../../shopify/models/line-item.model';
import { addToCart, buyItNow, loadCart } from './cart.thunk';

export interface ICartState {
  lineItems: LineItem[] | null;
  cart: Cart | undefined;
  checkout: Checkout | null;
  loading: boolean;
  error: ApiError | null;
}

const initialState: ICartState = {
  lineItems: null,
  cart: undefined,
  checkout: null,
  loading: false,
  error: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    resetCart: () => initialState,
  },
  extraReducers: (builder) => {
    // Add to Cart
    builder
      .addCase(addToCart.pending, (state: ICartState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state: ICartState, action) => {
        state.loading = false;
        state.lineItems = action.payload.lineItems;
      })
      .addCase(addToCart.rejected, (state: ICartState, action) => {
        state.loading = false;
        state.error = action.payload || ApiError.Other;
      })

      // Buy It Now
      .addCase(buyItNow.pending, (state: ICartState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(buyItNow.fulfilled, (state: ICartState, action) => {
        state.loading = false;
        state.checkout = action.payload;
      })
      .addCase(buyItNow.rejected, (state: ICartState, action) => {
        state.loading = false;
        state.error = action.payload || ApiError.Other;
      })

      // Load Cart
      .addCase(loadCart.pending, (state: ICartState) => {
        state.loading = true;
      })
      .addCase(loadCart.fulfilled, (state: ICartState, action) => {
        state.loading = false;
        state.cart = action.payload;
      })
      .addCase(loadCart.rejected, (state: ICartState, action) => {
        state.loading = false;
        state.error = action.payload || ApiError.Other;
      });
  },
});

export const cartReducer = cartSlice.reducer;
